import { useRequest, useUnmount } from 'ahooks'
import { startAuthentication } from '@simplewebauthn/browser'
import { isError } from 'lodash-es'

import { asyncSuccessResolver, toast } from '$app/utilities'

import { errorMessageResolver, successResolver } from '$services/api/common'
import { AnonymousAuthAPI } from '$services/api/anonymous-auth'

import { addToken } from '$store/session'
import { Token } from '$store/session/tokens'

import { useSuccessLogin } from './useSuccessLogin'

const anonymousAuthApi = new AnonymousAuthAPI(null)

const toastID = 'passkey-login'

export const usePasskeyLogin = () => {
  const { passkeyLoginOption, passkeyLoginVerify } = anonymousAuthApi

  const onLoginSuccess = useSuccessLogin()

  const onTokenSuccess = (token: Token) => {
    const id = addToken(token)

    onLoginSuccess(id)
  }

  const doAuthenticate = async () => {
    const optionsRes = await passkeyLoginOption()
    const { data: options } = successResolver(optionsRes)

    const payloads = await startAuthentication({ optionsJSON: options })

    return { payloads, options }
  }

  const doVerify = async (res: Awaited<ReturnType<typeof doAuthenticate>>) => {
    if (!res) return

    const {
      payloads,
      options: { reference_code }
    } = res

    toast.promise(
      verifyReq.runAsync({
        reference_code,
        payloads
      }),
      {
        id: toastID,
        loading: 'Verifying..',
        error: errorMessageResolver,
        success: 'Authenticated'
      }
    )
  }

  const authenticateReq = useRequest(doAuthenticate, {
    manual: true,
    onSuccess: doVerify
  })

  const verifyReq = useRequest(
    asyncSuccessResolver(passkeyLoginVerify, successResolver),
    {
      manual: true,
      onSuccess: ({ data: { auth_token } }) => onTokenSuccess(auth_token)
    }
  )

  const requests = [authenticateReq, verifyReq]

  const loading = requests.map(({ loading }) => loading).includes(true)

  const onReset = () => {
    requests.forEach(({ cancel, mutate }) => {
      cancel()
      mutate()
    })

    toast.dismiss(toastID)
  }

  const authenticate = async () => {
    toast.promise(authenticateReq.runAsync(), {
      id: toastID,
      loading: 'Waiting for you to interact with the browser...',
      error: e => {
        if (isError(e) && e.name === 'NotAllowedError') {
          return 'Authentication timed out or was cancelled'
        }

        return errorMessageResolver(e, 'Failed to authenticate')
      },
      success: 'Continued to verification...'
    })
  }

  /**
   * Clear passkey state when unmounting current component.
   * This stops any browser passkey popups from staying open
   * when the user goes to another page.
   */
  useUnmount(onReset)

  return {
    onClick: authenticate,
    disabled: loading,
    onReset
  }
}

export default usePasskeyLogin
